import { PageGridColumn, PageGridRow } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useMount } from "shared-utils";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { usePlatformConnectors } from "../../../queries/connectors/queries";
import { ConnectorCategory } from "../../../types";
import { PlatformsAccordion } from "../components";
import { connectedConnectorResourcesItemProps } from "../components/platforms-resource-items";

import type { Step } from "./forms";
import { ConnectForm, HasAdditionalPlatformForm, SelectAdditionalSourceForm } from "./forms";
import { NoPlatformReasonForm } from "./forms/no-platform-reason-form";
import { InfoBanner } from "./helpers/info-banner";
import { PageDescription } from "./helpers/page-description";
import { MESSAGES } from "./message-definitions";

export interface RevenueSourceProps {
  onBack: () => void;
  publicIdentifier: string;
  airData: FundingApplicationSchemas.AdditionalRevenueSourceAIRSchema;
}

export const RevenueSource = ({ onBack, publicIdentifier, airData }: RevenueSourceProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: storeConnectors } = usePlatformConnectors(customerDomain, {
    category: ConnectorCategory.ECOMMERCE,
    skipConnections: false,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const connectionInProgress = searchParams.get("platform");
  const oauthError = searchParams.get("oauth-error");
  const questionaireStep = searchParams.get("step");

  const setQuestionaireStep = (step: Step, connector?: string) => {
    if (connector) {
      setSearchParams({ step, platform: connector });
      return;
    }
    setSearchParams({ step });
  };

  useMount(() => {
    if (!questionaireStep) {
      const params: { [key: string]: string } = {
        step: connectionInProgress ? "connect_additional_source" : "has_additional_platform",
      };
      if (connectionInProgress) {
        params["platform"] = connectionInProgress;
        if (oauthError) {
          params["oauth-error"] = oauthError;
        }
      }
      setSearchParams(params, { replace: true }); // Replace the current history entry
    }
  });

  const storeConnectorResources = connectedConnectorResourcesItemProps(intl, storeConnectors);

  return (
    <PageGridColumn $gap="8">
      <PageGridRow>
        <PageDescription data-chromatic="ignore" airData={airData} />
      </PageGridRow>
      <InfoBanner data-chromatic="ignore" airData={airData} />
      {questionaireStep === "has_additional_platform" && (
        <PageGridRow>
          <HasAdditionalPlatformForm setQuestionaireStep={setQuestionaireStep} onBack={onBack} />
        </PageGridRow>
      )}
      {questionaireStep === "no_platform_reason" && (
        <PageGridRow>
          <NoPlatformReasonForm
            setQuestionaireStep={setQuestionaireStep}
            airPublicIdentifier={publicIdentifier}
            onSubmit={onBack}
          />
        </PageGridRow>
      )}
      {questionaireStep === "select_additional_source" && (
        <PageGridRow>
          <SelectAdditionalSourceForm
            setQuestionaireStep={setQuestionaireStep}
            airPublicIdentifier={publicIdentifier}
            onSubmit={onBack}
          />
        </PageGridRow>
      )}
      {questionaireStep === "connect_additional_source" && connectionInProgress && (
        <PageGridRow>
          <ConnectForm
            connectorSlug={connectionInProgress}
            airPublicIdentifier={publicIdentifier}
            setQuestionaireStep={setQuestionaireStep}
          />
        </PageGridRow>
      )}
      <PageGridRow>
        <PlatformsAccordion
          header={intl.formatMessage(MESSAGES.platformsAccordionTitle)}
          platformItems={storeConnectorResources}
        />
      </PageGridRow>
    </PageGridColumn>
  );
};
